import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWizard } from '../useWizard';
import LookupMultiSelect, { type LookupOption } from '../../shared/LookupMultiSelect';
import { clinicApi } from '../../../services/clinicApi';
import { procedureApi } from '@features/procedures/services/procedureApi';

/**
 * Capabilities & Services step. Clinic Network decision 2026-09-16 (Morgan):
 * the Emergency Room / After Hours / PAT / X-Ray tiles and the fit-test
 * dropdowns that used to sit above these lists are gone — each attribute has
 * exactly one home here (After Hours and X-ray On Site are capabilities,
 * Emergency Room is a category, PAT and the fit tests are procedures). The
 * server derives the legacy clinic flag columns from these selections.
 */
const CapabilitiesStep: React.FC = () => {
  const {
    state,
    updateCategoryTypeIds,
    updateCapabilityTypeIds,
    updateServiceProcedureIds,
  } = useWizard();

  const categoriesQuery = useQuery({
    queryKey: ['categoryTypes'],
    queryFn: () => clinicApi.getCategoryTypes(),
  });
  const capabilitiesQuery = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: () => clinicApi.getCapabilityTypes(),
  });
  const proceduresQuery = useQuery({
    queryKey: ['procedures', 'mappedOnly'],
    queryFn: () => procedureApi.getProcedures(undefined, true),
  });

  const categoryOptions: LookupOption[] = useMemo(
    () =>
      (categoriesQuery.data ?? [])
        .filter((c) => c.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((c) => ({ id: c.id, name: c.name })),
    [categoriesQuery.data]
  );

  const capabilityOptions: LookupOption[] = useMemo(
    () =>
      (capabilitiesQuery.data ?? [])
        .filter((c) => c.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((c) => ({ id: c.id, name: c.name })),
    [capabilitiesQuery.data]
  );

  const procedureOptions: LookupOption[] = useMemo(
    () =>
      (proceduresQuery.data ?? []).map((p) => ({
        id: p.procedureId,
        name: p.effectiveName,
        group: p.groupName,
        code: p.procedureCode,
      })),
    [proceduresQuery.data]
  );

  return (
    <div className="wizard-step">
      <h2>Capabilities &amp; Services</h2>
      <p className="step-description">Specify the categories, capabilities, and services available at this clinic.</p>

      <div className="verify-section">
        <h3 className="verify-section-heading">Categories</h3>
        <LookupMultiSelect
          options={categoryOptions}
          selectedIds={state.categoryTypeIds}
          onChange={updateCategoryTypeIds}
          itemNoun="category"
          itemNounPlural="categories"
          isLoading={categoriesQuery.isLoading}
          loadError={categoriesQuery.isError ? 'Failed to load categories.' : null}
          onRetry={() => categoriesQuery.refetch()}
        />
      </div>

      <div className="verify-section-divider">
        <h3 className="verify-section-heading">Capabilities</h3>
        <LookupMultiSelect
          options={capabilityOptions}
          selectedIds={state.capabilityTypeIds}
          onChange={updateCapabilityTypeIds}
          itemNoun="capability"
          itemNounPlural="capabilities"
          isLoading={capabilitiesQuery.isLoading}
          loadError={capabilitiesQuery.isError ? 'Failed to load capabilities.' : null}
          onRetry={() => capabilitiesQuery.refetch()}
        />
      </div>

      <div className="verify-section-divider">
        <h3 className="verify-section-heading">Services / Procedures</h3>
        <LookupMultiSelect
          options={procedureOptions}
          selectedIds={state.serviceProcedureIds}
          onChange={updateServiceProcedureIds}
          itemNoun="service"
          groupBy
          isLoading={proceduresQuery.isLoading}
          loadError={proceduresQuery.isError ? 'Failed to load services.' : null}
          onRetry={() => proceduresQuery.refetch()}
        />
      </div>
    </div>
  );
};

export default CapabilitiesStep;
